"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PricePropagationJobSchema = exports.PricePropagationJobTotalsSchema = exports.PricePropagationJobErrorSchema = exports.PricePropagationJobStatusSchema = exports.PRICE_PROPAGATION_JOB_STATUSES = void 0;
exports.isTerminalJobStatus = isTerminalJobStatus;
const zod_1 = require("zod");
const pricePropagation_1 = require("./pricePropagation");
/**
 * PACKAGE PRICE PROPAGATION — Task 3.5c of the price-propagation plan
 * (`docs/superpowers/plans/2026-09-04-package-price-propagation.md`, §3.5c).
 *
 * THE JOB RECORD SHAPE for a background price-propagation run. Pure schema
 * only — no worker, no apply logic, no SQS, no endpoint. Those are 3.5b/3.5d
 * and Task 4, deliberately not built here. This module is the contract they
 * all bind to: schema, status vocabulary, and progress reporting shape.
 *
 * Why the shape looks like this (product owner's request, verbatim): an
 * estate-wide Stripe repricing fans out across every affected tenant and
 * cannot complete inside an API request — "make sure this will execute
 * smoothly in background because this stripe update will take longer …
 * after price update progress should appear to see the process is completed
 * and how many clients get affected." `totals.processed / totals.tenants`
 * IS the progress bar. `succeeded`, `blocked` and `failed` answer "how many
 * clients got affected" — and they are counted SEPARATELY, never rolled
 * into one number, because an operator acts on each differently: a
 * `succeeded` tenant needs nothing; a `blocked` one (schedule-owned,
 * suspended, no card — see the dry-run blocker table in §3) was correctly
 * left alone; a `failed` one needs investigation.
 */
/**
 * The closed status vocabulary. A Zod enum (not a bare `z.string()`) so an
 * unknown status is rejected AT THE BOUNDARY — a typo or a future status
 * added on one side of a poller/worker split fails loudly instead of
 * silently falling through an `if/else` chain that assumes there are only
 * ever five values.
 *
 * Exported as a plain runtime array (not just the derived type) so UI code
 * — a status filter dropdown, a legend — can iterate the real list instead
 * of re-typing it and drifting out of sync.
 */
exports.PRICE_PROPAGATION_JOB_STATUSES = [
    'queued',
    'running',
    'completed',
    'completed_with_errors',
    'failed',
];
exports.PricePropagationJobStatusSchema = zod_1.z.enum(exports.PRICE_PROPAGATION_JOB_STATUSES);
/** One tenant that errored out, and why. Never thrown away — an operator reads this list to decide what to retry. */
exports.PricePropagationJobErrorSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    reason: zod_1.z.string().min(1),
});
/**
 * Progress + outcome counters. All non-negative integers.
 *
 * Two cross-field invariants are enforced here, not left as an unchecked
 * assumption:
 *   - `processed <= tenants` — processed can never overtake the scope it
 *     was resolved against.
 *   - `succeeded + blocked + failed <= processed` — every accounted-for
 *     tenant is exactly one of those three outcomes (or not yet processed);
 *     the three cannot together exceed how many have actually been visited.
 * These are deliberately `<=`, not `===` at every instant — a worker mid-way
 * through one tenant may have incremented `processed` fractionally ahead of
 * recording that tenant's outcome, and refusing to let the SUM overtake
 * `processed` is what actually matters for the progress bar to never show
 * more "done" than "seen". See the task report for why this was enforced
 * rather than left as a convention everyone assumes but nobody checks.
 */
exports.PricePropagationJobTotalsSchema = zod_1.z
    .object({
    tenants: zod_1.z.number().int().nonnegative(),
    processed: zod_1.z.number().int().nonnegative(),
    succeeded: zod_1.z.number().int().nonnegative(),
    blocked: zod_1.z.number().int().nonnegative(),
    failed: zod_1.z.number().int().nonnegative(),
})
    .refine((t) => t.processed <= t.tenants, {
    message: 'totals.processed cannot exceed totals.tenants',
    path: ['processed'],
})
    .refine((t) => t.succeeded + t.blocked + t.failed <= t.processed, {
    message: 'totals.succeeded + totals.blocked + totals.failed cannot exceed totals.processed',
    path: ['succeeded'],
});
/**
 * The persisted job record. Reuses `PackagePriceChangeSchema` /
 * `DealPriceAdjustmentSchema` from Task 1 (`./pricePropagation`) for
 * `changes` and `adjustments` rather than redefining their shape — those
 * are the "what was asked for" and "what Task 1 computed, frozen at
 * enqueue" respectively; nothing about a job record makes a NEW claim about
 * either shape.
 */
exports.PricePropagationJobSchema = zod_1.z.object({
    jobId: zod_1.z.string().min(1),
    status: exports.PricePropagationJobStatusSchema,
    /** What was asked for. */
    changes: zod_1.z.array(pricePropagation_1.PackagePriceChangeSchema),
    /** Task 1's output, frozen at enqueue time — a deal edited mid-run must not retroactively change what this job applies. */
    adjustments: zod_1.z.array(pricePropagation_1.DealPriceAdjustmentSchema),
    totals: exports.PricePropagationJobTotalsSchema,
    /**
     * THE RESUME POINT. Optional because a job that has not yet paged through
     * Task 2's tenant resolver has none. This holds the tenant-resolver's page
     * cursor (`tenantsAffectedBy`'s `nextCursor`) — a job that dies at tenant
     * 300 of 900 resumes FROM HERE instead of re-charging the first 300 all
     * over again.
     */
    cursor: zod_1.z.string().optional(),
    /** Defaults to `[]`, never `undefined` — so a consumer can always map/render this without a null check. */
    errors: zod_1.z.array(exports.PricePropagationJobErrorSchema).default([]),
    /** ISO strings. A `queued` job has neither — it hasn't started, so it can't have finished. */
    startedAt: zod_1.z.string().optional(),
    finishedAt: zod_1.z.string().optional(),
    requestedBy: zod_1.z.string().min(1),
});
const TERMINAL_JOB_STATUSES = new Set([
    'completed',
    'completed_with_errors',
    'failed',
]);
/**
 * The one place a poller asks "should I stop polling". `queued` and
 * `running` are the only non-terminal statuses; the other three are all
 * terminal, whatever else distinguishes them (a clean finish, a partial
 * finish, or an outright failure). Kept here so the UI never re-derives
 * this list itself and risks missing a status when a sixth one is added.
 */
function isTerminalJobStatus(status) {
    return TERMINAL_JOB_STATUSES.has(status);
}
//# sourceMappingURL=pricePropagationJob.js.map